import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ClientsList.css';

function ClientsList() {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const navigate = useNavigate();

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/clients', {
        params: {
          page,
          pageSize,
          search: searchQuery.trim(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setClients(response.data.clients || []);
      setPagination(response.data.pagination || { page, pageSize, total: 0, totalPages: 1 });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load clients.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return (
    <div className="clients-page app-fixed-page">
      <div className="clients-header app-page-header">
        <div className="app-title-group">
          <button className="back-link app-back-button" onClick={() => navigate('/')} aria-label="Back to home">
            Back
          </button>
          <div className="app-title-copy">
            <h2>Clients</h2>
          <p>Manage clients, search records, and add new client entries.</p>
          </div>
        </div>
        <div className="app-header-actions">
          <button className="btn-new-client" onClick={() => navigate('/clients/new')}>
            + New client
          </button>
        </div>
      </div>

      <div className="clients-toolbar">
        <input
          className="clients-search"
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setPage(1);
            setSearchQuery(e.target.value);
          }}
          placeholder="Search by name, address, phone, email"
        />
        <div className="clients-actions">
          <select
            className="clients-page-size"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(parseInt(e.target.value, 10));
            }}
          >
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
          </select>
          <button className="clients-action" onClick={fetchClients}>Refresh</button>
        </div>
      </div>

      <div className="app-scroll-results">
        <div className="clients-table-wrapper app-grid-scroll">
          {loading ? (
            <div className="clients-loading">Loading clients...</div>
          ) : error ? (
            <div className="clients-error">{error}</div>
          ) : (
            <table className="clients-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>App</th>
                  <th>Email Verify</th>
                  <th>Address</th>
                  <th>City</th>
                  <th>Zip</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="empty-message">
                      No clients found. Try a different search term or add a new client.
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => (
                    <tr key={client.ClientId}>
                      <td>{client.ClientName}</td>
                      <td>
                        <span className={`clients-badge ${client.MobileAccessEnabled ? 'clients-badge-green' : 'clients-badge-muted'}`}>
                          {client.MobileAccessEnabled ? 'Enabled' : 'Off'}
                        </span>
                      </td>
                      <td>
                        <span className={`clients-badge ${client.EmailVerified ? 'clients-badge-blue' : 'clients-badge-amber'}`}>
                          {client.EmailVerified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td>{client.Address || '-'}</td>
                      <td>{client.City || '-'}</td>
                      <td>{client.ZipCode || '-'}</td>
                      <td>{client.Phone || '-'}</td>
                      <td>{client.Email || '-'}</td>
                      <td>
                        <button className="row-button" onClick={() => navigate(`/clients/${client.ClientId}`)}>
                          {'\u2192'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && (
          <div className="clients-pagination">
            <div className="clients-page-info">
              Showing page {pagination.page} of {pagination.totalPages} | Total rows: {pagination.total}
            </div>
            <div className="clients-page-buttons">
              <button
                className="clients-action"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={pagination.page <= 1}
              >
                Previous
              </button>
              <button
                className="clients-action"
                onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientsList;
